import { PDFDocument } from 'pdf-lib';
import { saveAs } from 'file-saver';

export class PDFPageRemovalService {
  static async loadPDF(file) {
    try {
      const arrayBuffer = await file.arrayBuffer();
      const pdfDoc = await PDFDocument.load(arrayBuffer);
      return {
        success: true,
        pdfDoc,
        pageCount: pdfDoc.getPageCount(),
        fileName: file.name
      };
    } catch (error) {
      console.error('Error loading PDF:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  static async generatePageThumbnails(pdfDoc, maxThumbnails = 10) {
    try {
      const pageCount = pdfDoc.getPageCount();
      const thumbnails = [];

      // Limit thumbnails for performance
      const step = Math.max(1, Math.floor(pageCount / maxThumbnails));

      for (let i = 0; i < pageCount; i += step) {
        try {
          const page = pdfDoc.getPage(i);
          const { width, height } = page.getSize();

          thumbnails.push({
            pageNumber: i + 1,
            width: width,
            height: height,
            aspectRatio: width / height
          });

          if (thumbnails.length >= maxThumbnails) break;
        } catch (error) {
          console.warn(`Error generating thumbnail for page ${i + 1}:`, error);
        }
      }

      return { success: true, thumbnails, totalPages: pageCount };
    } catch (error) {
      console.error('Error generating thumbnails:', error);
      return { success: false, error: error.message };
    }
  }

  static async removePages(pdfDoc, pagesToRemove, options = {}) {
    try {
      const totalPages = pdfDoc.getPageCount();

      // Validate page numbers
      const validPagesToRemove = pagesToRemove
        .filter(pageNum => pageNum >= 1 && pageNum <= totalPages)
        .sort((a, b) => b - a); // Sort in descending order for safe removal

      if (validPagesToRemove.length === 0) {
        throw new Error('No valid pages to remove');
      }

      if (validPagesToRemove.length === totalPages) {
        throw new Error('Cannot remove all pages from PDF');
      }

      // Remove pages (in reverse order to maintain indices)
      for (const pageNum of validPagesToRemove) {
        pdfDoc.removePage(pageNum - 1); // Convert to 0-based index
      }

      return {
        success: true,
        removedCount: validPagesToRemove.length,
        remainingPages: totalPages - validPagesToRemove.length,
        removedPages: validPagesToRemove.sort((a, b) => a - b)
      };
    } catch (error) {
      console.error('Error removing pages:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  static async keepOnlyPages(pdfDoc, pagesToKeep) {
    try {
      const totalPages = pdfDoc.getPageCount();

      // Validate and sort pages to keep
      const validPagesToKeep = [...new Set(pagesToKeep)]
        .filter(pageNum => pageNum >= 1 && pageNum <= totalPages)
        .sort((a, b) => a - b);

      if (validPagesToKeep.length === 0) {
        throw new Error('No valid pages to keep');
      }

      // Create new PDF with only the pages to keep
      const newPdfDoc = await PDFDocument.create();

      // Copy pages in the order specified
      for (const pageNum of validPagesToKeep) {
        const [copiedPage] = await newPdfDoc.copyPages(pdfDoc, [pageNum - 1]);
        newPdfDoc.addPage(copiedPage);
      }

      return {
        success: true,
        pdfDoc: newPdfDoc,
        keptCount: validPagesToKeep.length,
        removedCount: totalPages - validPagesToKeep.length,
        keptPages: validPagesToKeep
      };
    } catch (error) {
      console.error('Error keeping pages:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Quick removal methods
  static getPageNumbersToRemove(totalPages, removalType, options = {}) {
    const { count = 1, startPage = 1, endPage = totalPages } = options;

    switch (removalType) {
      case 'first':
        return [1];

      case 'last':
        return [totalPages];

      case 'first-n':
        return Array.from({ length: Math.min(count, totalPages - 1) }, (_, i) => i + 1);

      case 'last-n':
        const startIdx = Math.max(1, totalPages - count + 1);
        return Array.from({ length: totalPages - startIdx + 1 }, (_, i) => startIdx + i);

      case 'range':
        const start = Math.max(1, Math.min(startPage, totalPages));
        const end = Math.min(totalPages, Math.max(startPage, endPage));
        return Array.from({ length: end - start + 1 }, (_, i) => start + i);

      case 'middle-n':
        const middleStart = Math.max(1, Math.floor((totalPages - count) / 2) + 1);
        const middleEnd = Math.min(totalPages, middleStart + count - 1);
        return Array.from({ length: middleEnd - middleStart + 1 }, (_, i) => middleStart + i);

      default:
        return [];
    }
  }

  static async quickRemoval(pdfDoc, removalType, options = {}) {
    const totalPages = pdfDoc.getPageCount();
    const pagesToRemove = this.getPageNumbersToRemove(totalPages, removalType, options);

    if (pagesToRemove.length === 0) {
      return { success: false, error: 'Invalid removal type or parameters' };
    }

    return await this.removePages(pdfDoc, pagesToRemove, options);
  }

  static async savePDF(pdfDoc, fileName = 'modified.pdf') {
    try {
      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      saveAs(blob, fileName);

      return {
        success: true,
        fileName,
        size: pdfBytes.length
      };
    } catch (error) {
      console.error('Error saving PDF:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  static generateOutputFileName(originalFileName, removalInfo) {
    const baseName = originalFileName.replace(/\.pdf$/i, '');
    const timestamp = new Date().toISOString().slice(0, 10);

    let suffix = '';
    if (removalInfo.removedCount) {
      suffix = `_removed-${removalInfo.removedCount}-pages`;
    } else if (removalInfo.keptCount) {
      suffix = `_kept-${removalInfo.keptCount}-pages`;
    }

    return `${baseName}${suffix}_${timestamp}.pdf`;
  }

  static async processRemoval(file, removalConfig, onProgress = null) {
    try {
      // Step 1: Load PDF
      if (onProgress) onProgress(10, 'Loading PDF...');
      const loadResult = await this.loadPDF(file);
      if (!loadResult.success) {
        throw new Error(loadResult.error);
      }

      const { pdfDoc, pageCount, fileName } = loadResult;

      // Step 2: Generate thumbnails
      if (onProgress) onProgress(20, 'Analyzing pages...');
      const thumbnailResult = await this.generatePageThumbnails(pdfDoc);
      if (!thumbnailResult.success) {
        console.warn('Could not generate thumbnails:', thumbnailResult.error);
      }

      // Step 3: Process removal
      if (onProgress) onProgress(50, 'Removing pages...');

      let removalResult;
      if (removalConfig.type === 'custom' && removalConfig.pagesToRemove) {
        removalResult = await this.removePages(pdfDoc, removalConfig.pagesToRemove);
      } else if (removalConfig.type === 'keep' && removalConfig.pagesToKeep) {
        removalResult = await this.keepOnlyPages(pdfDoc, removalConfig.pagesToKeep);
        if (removalResult.success) {
          // Replace original doc with new doc
          Object.assign(loadResult, { pdfDoc: removalResult.pdfDoc });
        }
      } else {
        removalResult = await this.quickRemoval(pdfDoc, removalConfig.type, removalConfig.options);
      }

      if (!removalResult.success) {
        throw new Error(removalResult.error);
      }

      // Step 4: Save PDF
      if (onProgress) onProgress(80, 'Generating PDF...');
      const outputFileName = this.generateOutputFileName(fileName, removalResult);
      const saveResult = await this.savePDF(loadResult.pdfDoc, outputFileName);

      if (!saveResult.success) {
        throw new Error(saveResult.error);
      }

      if (onProgress) onProgress(100, 'Complete!');

      return {
        success: true,
        originalPages: pageCount,
        ...removalResult,
        fileName: outputFileName,
        fileSize: saveResult.size,
        thumbnails: thumbnailResult.thumbnails || []
      };

    } catch (error) {
      console.error('PDF processing error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  }

  // Cleanup resources
  static cleanup() {
    // Force garbage collection if available (development mode)
    if (window.gc) {
      window.gc();
    }
  }
}